import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { PatientGridRow } from "@/models/missionControl";
import { missionControlService, } from "@/services/missionControlService";
const GRID_KEYS = ["scope", "sort", "direction", "search", "assigned", "page", "page_size"];
const RETIRED_KEYS = ["view", "outcome", "patient", "kind", "date_from", "date_to"];
const PAGE_CACHE_LIMIT = 20;
const PAGE_CACHE = new Map();
const PREFETCHING = new Set();
const PATIENT_CACHE = new Map();
const PATIENT_CACHE_LIMIT = 30;
const PATIENT_FRESH_MS = 60_000;
const GRID_SORTS = ["attention", "activity", "name", "expiry"];
function initialSort(query) {
    const asked = typeof query.sort === "string" && GRID_SORTS.includes(query.sort) ? query.sort : null;
    const sort = asked ?? (query.scope === "all" ? "name" : "attention");
    const ascending = sort === "name" || sort === "expiry";
    const direction = query.direction === "asc" || query.direction === "desc" ? query.direction : ascending ? "asc" : "desc";
    return { sort, direction };
}
let lastGridOrder = null;
export function gridOrder() {
    return lastGridOrder;
}
function cachePut(key, page) {
    PAGE_CACHE.delete(key);
    PAGE_CACHE.set(key, page);
    if (PAGE_CACHE.size > PAGE_CACHE_LIMIT)
        PAGE_CACHE.delete(PAGE_CACHE.keys().next().value);
}
export function paramsFor(query) {
    const params = {};
    for (const key of GRID_KEYS) {
        const value = query[key];
        if (typeof value === "string" && value !== "")
            params[key] = value;
    }
    return params;
}
function keyOf(params) {
    return JSON.stringify(params);
}
/** Load the patient grid into the page cache, so opening it from the sidebar paints at once. */
export function prefetchMissionControl() {
    const params = paramsFor({});
    const key = keyOf(params);
    if (PAGE_CACHE.has(key) || PREFETCHING.has(key))
        return;
    PREFETCHING.add(key);
    missionControlService
        .patients(params)
        .then((page) => cachePut(key, page))
        .catch(() => { })
        .finally(() => PREFETCHING.delete(key));
}
export function cachedPatient(id) {
    return PATIENT_CACHE.get(id) ?? null;
}
export function storePatient(id, part) {
    const current = PATIENT_CACHE.get(id) ?? { at: Date.now() };
    PATIENT_CACHE.delete(id);
    PATIENT_CACHE.set(id, { ...current, ...part, at: Date.now() });
    if (PATIENT_CACHE.size > PATIENT_CACHE_LIMIT)
        PATIENT_CACHE.delete(PATIENT_CACHE.keys().next().value);
}
export function forgetPatient(id) {
    PATIENT_CACHE.delete(id);
}
/** Warm a patient's header and journey while the pointer rests on their row; reads only, never logs a view. */
export function prefetchPatient(id) {
    const cached = PATIENT_CACHE.get(id);
    if (cached?.detail && cached.journey && Date.now() - cached.at < PATIENT_FRESH_MS)
        return;
    Promise.all([missionControlService.patient(id), missionControlService.journey(id)])
        .then(([detail, journey]) => storePatient(id, { detail, journey }))
        .catch(() => { });
}
function isCancel(error) {
    const code = error?.code;
    const inner = error?.error?.code;
    return code === "ERR_CANCELED" || inner === "ERR_CANCELED";
}
export function useMissionControlGrid() {
    const route = useRoute();
    const router = useRouter();
    const params = computed(() => paramsFor(route.query));
    const patientRows = ref([]);
    const visibleModules = ref([]);
    const totalCount = ref(0);
    const initial = initialSort(route.query);
    const sort = ref(initial.sort);
    const direction = ref(initial.direction);
    const scope = ref(route.query.scope === "all" ? "all" : "open");
    const counts = ref(null);
    const recent = ref([]);
    const loading = ref(false);
    const firstLoad = ref(true);
    const error = ref(null);
    const notEnabled = ref(false);
    const lastLoadedAt = ref(null);
    let controller = null;
    function apply(page) {
        totalCount.value = page.count;
        sort.value = page.sort;
        direction.value = page.direction;
        scope.value = page.scope;
        patientRows.value = PatientGridRow.fromPage(page);
        visibleModules.value = page.visibleModules ?? [];
        lastGridOrder = {
            ids: page.results.map((row) => row.id),
            query: { ...route.query },
            page: Number(params.value.page ?? 1),
            pageCount: Math.max(Math.ceil(page.count / Number(params.value.page_size ?? 25)), 1),
        };
    }
    async function fetchRows({ silent = false } = {}) {
        const forParams = params.value;
        const key = keyOf(forParams);
        const own = new AbortController();
        controller?.abort();
        controller = own;
        const cached = PAGE_CACHE.get(key);
        if (cached) {
            apply(cached);
            firstLoad.value = false;
        }
        loading.value = !silent && !cached;
        error.value = null;
        try {
            const page = await missionControlService.patients(forParams, own.signal);
            cachePut(key, page);
            if (key !== keyOf(params.value))
                return;
            apply(page);
            notEnabled.value = false;
            lastLoadedAt.value = new Date();
        }
        catch (caught) {
            if (isCancel(caught))
                return;
            const status = caught?.response?.status;
            if (status === 404 || caught?.status === 404)
                notEnabled.value = true;
            else
                error.value = "Mission Control could not load. Try again.";
        }
        finally {
            if (controller === own) {
                loading.value = false;
                firstLoad.value = false;
            }
        }
    }
    async function fetchCounts() {
        try {
            counts.value = await missionControlService.counts();
        }
        catch {
            counts.value = counts.value ?? null;
        }
    }
    async function fetchRecent() {
        try {
            recent.value = (await missionControlService.recent()).results;
        }
        catch {
            recent.value = [];
        }
    }
    function navigate(changes, { keepPage = false, replace = false } = {}) {
        const next = { ...route.query };
        for (const [key, value] of Object.entries(changes)) {
            if (value === null || value === "")
                delete next[key];
            else
                next[key] = value;
        }
        if (!keepPage)
            delete next.page;
        const target = { query: next };
        return replace ? router.replace(target) : router.push(target);
    }
    function setSearch(value) {
        return navigate({ search: value.trim() || null }, { replace: true });
    }
    function setSort(value) {
        const [key, dir] = value.split(":");
        return navigate({ sort: key || null, direction: dir || null });
    }
    function setPage(page) {
        return navigate({ page: page > 1 ? String(page) : null }, { keepPage: true });
    }
    function evict() {
        for (const key of [...PAGE_CACHE.keys()])
            PAGE_CACHE.delete(key);
    }
    watch(() => RETIRED_KEYS.some((key) => key in route.query), (stale) => {
        if (!stale)
            return;
        const query = { ...route.query };
        for (const key of RETIRED_KEYS)
            delete query[key];
        router.replace({ query });
    }, { immediate: true });
    watch(() => keyOf(params.value), () => fetchRows(), { immediate: true });
    onBeforeUnmount(() => controller?.abort());
    return {
        params,
        patientRows,
        visibleModules,
        totalCount,
        sort,
        direction,
        scope,
        counts,
        recent,
        loading,
        firstLoad,
        error,
        notEnabled,
        lastLoadedAt,
        fetchRows,
        fetchCounts,
        fetchRecent,
        navigate,
        setSearch,
        setSort,
        setPage,
        evict,
    };
}
